import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { selectCtrl } from "@app/global";
import { useTranslation } from "react-i18next";

import { getChannels, getSearchResults } from "@server/requests";

import useKeydown from "@hooks/useKeydown";
import { InoKeyboard } from "@ino-ui/tv";

import HeadSearch from "./components/HeadSearch";
import ResultSearch from "./components/ResultSearch";

import "./styles/Search.scss";
import { setCtrl } from "@app/global";

export default memo(function Search({
  type,
  setShow,
  setUrl,
  setPipMode = () => {},
}) {
  const { t } = useTranslation();
  const ctrl = useSelector(selectCtrl);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const refInp = useRef(null);

  const [searchValue, setSearchValue] = useState("");
  const [control, setControl] = useState("keyboard");
  const [result, setResult] = useState([]);
  const [empty, setEmpty] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const refContentInp = useRef(null);
  const resultSearch = useMemo(() => result.map((item) => item), [result]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get("search");
    if (query) {
      setSearchValue(query);
      if (type === "live") {
        getResultChannels(query);
      } else {
        getResultContent(query);
      }
    }
  }, [location.search, type]);

  useEffect(() => {
    if (result.length === 0) {
      setEmpty(true);
    } else {
      setEmpty(false);
    }
  }, [result]);

  const getResultChannels = async (query) => {
    setEmpty(false);
    const searchAnd = { name: query };

    if (type !== "live") {
      searchAnd.show_protected = true;
    } else {
      delete searchAnd.show_protected;
    }

    const response = await getChannels({
      query: JSON.stringify({
        pagination: false,
        search_and: searchAnd,
        sort: ["position", "ASC"],
      }),
    });
    const parsedResponse = JSON.parse(response);
    const { error, message } = parsedResponse;

    if (!error) {
      if (message.length > 0) setResult(message.slice(0, 10));
      else setEmpty(true);
    } else {
      setEmpty(true);
    }
  };

  const getResultContent = async (query) => {
    try {
      const response = await getSearchResults({
        search: query,
        show_protected: true,
      });
      const parsedResponse = JSON.parse(response);
      const { error, message } = parsedResponse;
      if (!error) {
        const moviesSeries = [
          ...message.movies[0].content,
          ...message.tv_shows[0].content,
        ];
        if (moviesSeries.length > 0) {
          setResult(moviesSeries.slice(0, 10));
          setEmpty(false);
        } else {
          setResult([]);
          setEmpty(true);
        }
      } else {
        setResult([]);
        setEmpty(true);
        console.error("Error fetching search results:", error);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    let timer = null;
    if (searchValue) {
      timer = setTimeout(() => {
        if (type === "live") {
          getResultChannels(searchValue);
        } else {
          getResultContent(searchValue);
        }
      }, 500);
    } else {
      setResult([]);
      setEmpty(false);
    }
    return () => clearTimeout(timer);
  }, [searchValue, type]);

  useEffect(() => {
    if (refInp.current && type === "live") {
      refInp.current.focus();
    } else if (refContentInp.current && type === "content") {
      refContentInp.current.focus();
    }
  }, []);

  useEffect(() => {
    if (ctrl === "inp") {
      if (type === "live") {
        console.log("focus input here 1 useefect");
        refInp.current?.focus();
      } else if (type === "content") {
        refContentInp.current?.focus();
      }
    }
  }, [ctrl, refContentInp, type]);

  useEffect(() => {
    return () => {
      if (type === "live") {
        dispatch(setCtrl("channel"));
      } else {
        dispatch(setCtrl("moviesSeries"));
      }
    };
  }, [type]);

  const handleSearch = (e) => {
    const newValue = e.target.value.trim();
    setSearchValue(newValue);
    updateSearchQuery(newValue);
  };

  const updateSearchQuery = (query) => {
    const searchParams = new URLSearchParams(location.search);
    if (query) {
      searchParams.set("search", query);
    } else {
      searchParams.delete("search");
    }
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleKeyboardInput = (character) => {
    if (
      character === "backspace" ||
      character === "delete" ||
      character === "back"
    ) {
      setSearchValue((prev) => {
        const newValue = prev.slice(0, -1);
        updateSearchQuery(newValue);
        return newValue;
      });
      return;
    }

    if (character === "Clear") {
      setSearchValue("");
      updateSearchQuery("");
      return;
    }

    if (character && typeof character === "string" && character.length === 1) {
      setSearchValue((prev) => {
        const newValue = prev + character;
        updateSearchQuery(newValue);
        return newValue;
      });
    }
  };

  const handleKeyboardClose = () => {
    setShowKeyboard(false);
    if (empty) {
      dispatch(setCtrl("backBtn"));
    } else {
      dispatch(setCtrl("result"));
      setControl("result");
    }
  };

  const handleKeyboardSubmit = () => {
    setShowKeyboard(false);
    if (empty) {
      dispatch(setCtrl("backBtn"));
    } else {
      dispatch(setCtrl("result"));
      setControl("result");
    }
  };

  const openKeyboard = () => {
    setShowKeyboard(true);
    dispatch(setCtrl("inp"));
  };

  const setRemove = () => {
    const inputElement = refInp.current || refContentInp.current;
    if (inputElement) {
      const cursorPosition = inputElement.selectionStart;
      if (cursorPosition > 0) {
        const newValue =
          searchValue.slice(0, cursorPosition - 1) +
          searchValue.slice(cursorPosition);
        setSearchValue(newValue);
        updateSearchQuery(newValue);

        // Adjust the cursor position after removing the character
        setTimeout(() => {
          inputElement.setSelectionRange(
            cursorPosition - 1,
            cursorPosition - 1
          );
        }, 0); // Ensure the cursor position is updated after React state updates
      }
    }
  };

  useEffect(() => {
    const inputElement = refInp.current || refContentInp.current;
    if (inputElement) {
      inputElement.style.caretColor = "auto"; // Enable cursor blinking
    }
  }, [ctrl]);

  useKeydown(
    {
      isActive: ctrl !== "result" && !showKeyboard,
      up: () => {
        if (type === "live") {
          if (ctrl === "inp") {
            refInp.current.blur();
            dispatch(setCtrl("backBtn"));
          } else if (ctrl === "result") {
            const focusChangeTime = Date.now();
            const lastFocusChange = refInp.current?.lastFocusChangeTime || 0;

            if (focusChangeTime - lastFocusChange > 500) {
              dispatch(setCtrl("inp"));

              refInp.current.focus();
              refInp.current.lastFocusChangeTime = focusChangeTime;
            }
          }
        } else if (type === "content") {
          if (ctrl === "inp") {
            dispatch(setCtrl("backBtn"));
            refContentInp.current.blur();
          } else if (ctrl === "result") {
            const focusChangeTime = Date.now();
            const lastFocusChange =
              refContentInp.current?.lastFocusChangeTime || 0;

            if (focusChangeTime - lastFocusChange > 500) {
              dispatch(setCtrl("inp"));
              refContentInp.current.focus();
              refContentInp.current.lastFocusChangeTime = focusChangeTime;
            }
          }
        }
      },
      down: () => {
        if (type === "live") {
          if (ctrl === "result") return;

          if (ctrl === "inp") {
            if (empty) return;
            const focusChangeTime = Date.now();
            const lastFocusChange = refInp.current?.lastFocusChangeTime || 0;

            if (focusChangeTime - lastFocusChange > 500) {
              refInp.current.blur();
              dispatch(setCtrl("result"));
              setControl("result");
              refInp.current.lastFocusChangeTime = focusChangeTime;
            }
          } else if (ctrl === "backBtn") {
            dispatch(setCtrl("inp"));
            refInp.current.focus();
          }
        } else if (type === "content") {
          if (ctrl === "inp") {
            if (empty) return;
            const focusChangeTime = Date.now();
            const lastFocusChange =
              refContentInp.current?.lastFocusChangeTime || 0;

            if (focusChangeTime - lastFocusChange > 500) {
              refContentInp.current.blur();
              dispatch(setCtrl("result"));
              setControl("result");
              refContentInp.current.lastFocusChangeTime = focusChangeTime;
            }
          } else if (ctrl === "backBtn") {
            dispatch(setCtrl("inp"));
            refContentInp.current.focus();
          }
        }
      },
      ok: () => {
        if (ctrl === "inp") {
          openKeyboard();
        }
      },
      handleKeyPress: (key) => {
        if (ctrl !== "inp") return;

        if (["ArrowLeft", "ArrowRight"].includes(key)) {
          const inputElement = refInp.current || refContentInp.current;
          if (inputElement) {
            const cursorPosition = inputElement.selectionStart;
            const newPosition =
              key === "ArrowLeft"
                ? Math.max(0, cursorPosition - 1)
                : Math.min(inputElement.value.length, cursorPosition + 1);
            inputElement.setSelectionRange(newPosition, newPosition);
          }
          return;
        }

        if (["Backspace", "Delete"].includes(key)) return setRemove();

        if (/^[a-zA-Z0-9\s]$/.test(key)) {
          const newValue = searchValue + key;

          if (newValue.trim().length == 0) return;

          setSearchValue(newValue);
          updateSearchQuery(newValue);
        }
      },
      back: () => {
        if (!window.Android) {
          if (refContentInp.current?.value) {
            setRemove();
            return;
          } else {
            dispatch(setCtrl("backBtn"));
            refContentInp.current?.blur();
            return;
          }
        }

        if (type === "live") {
          refInp?.current?.blur();
          if (empty) {
            dispatch(setCtrl("backBtn"));
          } else {
            dispatch(setCtrl("result"));
          }
        } else if (type === "content") {
          refContentInp?.current?.blur();
          if (empty) {
            dispatch(setCtrl("backBtn"));
          } else {
            dispatch(setCtrl("result"));
          }
        }
      },
    },
    {
      timeout: 1000,
    }
  );

  return (
    <div className="parent-search">
      <HeadSearch
        setShowSearchHandler={() => {
          setShow(false);
          updateSearchQuery("");
        }}
        setControl={setControl}
        control={control === "result"}
      />
      <div
        className={`search-inp ${ctrl === "inp" ? "active" : ""}`}
        onClick={openKeyboard}
        role="button"
        tabIndex={0}
      >
        <span
          className={`search-inp-text ${!searchValue ? "placeholder" : ""}`}
        >
          {searchValue || t("Search")}
        </span>
        {ctrl === "inp" && <div className="cursor-blink"></div>}
      </div>
      <input
        type="text"
        ref={type === "live" ? refInp : refContentInp}
        style={{ display: "none" }}
        onChange={handleSearch}
        value={searchValue}
      />
      <ResultSearch
        empty={empty}
        refInp={refInp}
        setShow={setShow}
        result={resultSearch}
        setPipMode={setPipMode}
        type={type}
        setUrl={setUrl}
        setRemove={setRemove}
        setControl={setControl}
        onBackHandler={() => updateSearchQuery("")}
        ctrl={ctrl}
        control={
          control === "result" && !empty && ctrl !== "inp" && ctrl !== "backBtn"
        }
        changeCtrl={() => dispatch(setCtrl("inp"))}
      />

      {showKeyboard && (
        <InoKeyboard
          key={`search-keyboard-${showKeyboard ? "open" : "closed"}`}
          isOpen={showKeyboard}
          value={searchValue}
          onChange={handleKeyboardInput}
          onClose={handleKeyboardClose}
          onSubmit={handleKeyboardSubmit}
          infinite={true}
          placeholder={t("Search")}
          type="text"
          title={t("Search")}
          classNames="search-keyboard"
        />
      )}
    </div>
  );
});

const theme = {
  background: {
    primary: "#000",
    secondary: "#111",
  },
  text: {
    primary: "#fff",
    secondary: "#222",
  },
  border: {
    primary: "#333",
    secondary: "#444",
  },
};
